import { Component, HostBinding, HostListener, OnChanges, SimpleChanges } from '@angular/core';
import { ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { Input, Output, EventEmitter } from '@angular/core';

import { languageService } from '@unpispas/upp-base';
import { languageSupport } from '@unpispas/upp-base';
import { viewService } from '@unpispas/upp-base';
import { TicketView } from '@unpispas/upp-data';
import { TicketProductView } from '@unpispas/upp-data';
import { ViewRenderer } from '@unpispas/upp-wdgt';

export type TicketKeyboardField = 'amount' | 'price' | 'percent' | 'invite';

const LONG_PRESS_MS = 1000;

@Component({
    selector: 'upp-ticket-keyboard',
    changeDetection: ChangeDetectionStrategy.OnPush,
    templateUrl: './upp-keyboard.html',
    styleUrls: ['./upp-keyboard.scss'],
    providers: [{
        provide: ViewRenderer,
        useFactory: (change: ChangeDetectorRef) => new ViewRenderer(change),
        deps: [ChangeDetectorRef],
    }],
})
export class UppTicketKeyboardComponent extends languageSupport implements OnChanges {
    /****************************/
    /* INPUTS AND OUTPUTS       */
    /****************************/

    @Input() ticket: TicketView | null = null;
    @Input() ticketproduct: TicketProductView | null = null;

    @Output() Updated = new EventEmitter<void>();
    @Output() Closed = new EventEmitter<void>();

    /****************************/
    /* STATE                    */
    /****************************/

    private _displayValue = '';
    private _field: TicketKeyboardField = 'amount';
    private _pressTimeout: ReturnType<typeof setTimeout> | null = null;
    private _pressSide: 'add' | 'del' | null = null;
    private _pressed = false;
    private _ispressed = false;

    /****************************/
    /* UI GETTERS               */
    /****************************/

    get Display(): string {
        if (this._field === 'price') {
            return this._formatPriceDisplay(this._displayValue);
        }

        return this._displayValue;
    }

    get ShowsDecimalKey(): boolean {
        return this._field !== 'price';
    }

    get NumpadDisabled(): boolean {
        return this._field === 'invite';
    }

    IsEnabled(action: TicketKeyboardField): boolean {
        if (!this.ticket || !this.ticketproduct) {
            return false;
        }

        let enabled = false;

        switch (action) {
            case 'amount':
                enabled = true;
                break;

            case 'price':
                enabled = !this.ticketproduct.IsInvite;
                break;

            case 'percent':
                enabled = !this.ticketproduct.IsInvite;
                break;

            case 'invite':
                enabled = true;
                break;
        }

        return enabled;
    }

    get Field(): TicketKeyboardField {
        return this._field;
    }

    get ApplyCount(): number {
        return this.applyCount;
    }

    get ShowsApplyScope(): boolean {
        if (this._field === 'amount') {
            return false;
        }
        return this.TargetsMultiple;
    }

    get TargetsMultiple(): boolean {
        return this.lineAmount > 1;
    }

    get InviteTabActive(): boolean {
        return this._field === 'invite';
    }

    get ActionHintKey(): string | null {
        if (this._field === 'amount') {
            return '@ticket_product_keyboard_action_amount';
        }

        if (this._field === 'price') {
            return '@ticket_product_keyboard_action_price';
        }

        if (this._field === 'percent') {
            return '@ticket_product_keyboard_action_percent';
        }

        return null;
    }

    /****************************/
    /* CONSTRUCTOR / LIFECYCLE  */
    /****************************/

    constructor(private lang: languageService, private change: ViewRenderer, public view: viewService) {
        super(lang, change.cdref);
    }

    @HostBinding('class.mobile')
    get IsMobile(): boolean {
        return this.view.Mobile === true;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (!changes['ticket'] && !changes['ticketproduct']) {
            return;
        }
        this._displayValue = '';
        this._field = this.IsEnabled('price') ? 'amount' : 'invite';
        this.applyCount = this.minApply;
        if (this._pressTimeout !== null) {
            clearTimeout(this._pressTimeout);
            this._pressTimeout = null;
        }
        this._pressSide = null;
        this._pressed = false;
    }

    @HostListener('document:keydown.escape', ['$event'])
    OnDocumentEscape(event: KeyboardEvent): void {
        event.preventDefault();
        this._close();
    }

    /****************************/
    /* PUBLIC HANDLERS          */
    /****************************/

    OnDigit(event: Event, digit: string): void {
        event.stopPropagation();

        if (this.NumpadDisabled) {
            return;
        }

        if (digit === '.' && !this.ShowsDecimalKey) {
            return;
        }

        if (this._field === 'price') {
            if (!/^[0-9]$/.test(digit)) {
                return;
            }
            let centsDigits = (this._displayValue || '').replace(/\D/g, '');

            if (centsDigits.length >= 8) {
                return;
            }

            centsDigits = `${centsDigits}${digit}`;
            
            this._displayValue = centsDigits;
            this.change.markForCheck();
            
            return;
        }

        const maxLen = 14;
        let s = this._displayValue;

        if (digit === '.') {
            if (s.includes('.')) {
                return;
            }

            if (this._maxFractionDigits() <= 0) {
                return;
            }

            const candidate = s.length === 0 ? '0.' : `${s}.`;
            if (this._field === 'percent' && this._isPercentOverLimit(candidate)) {
                return;
            }

            this._displayValue = candidate;

            this.change.markForCheck();
            return;
        }

        if (!/^[0-9]$/.test(digit)) {
            return;
        }

        const dot = s.indexOf('.');
        if (dot >= 0) {
            const frac = s.slice(dot + 1);
            if (frac.length >= this._maxFractionDigits()) {
                return;
            }
        }

        if (s === '0' && dot < 0) {
            s = digit === '0' ? '0' : digit;
        } else {
            s = `${s}${digit}`;
        }

        if (s.length > maxLen) {
            return;
        }

        if (this._field === 'percent' && this._isPercentOverLimit(s)) {
            return;
        }

        this._displayValue = s;
        this.change.markForCheck();
    }

    OnClearDisplay(event: Event): void {
        event.stopPropagation();
        if (this.NumpadDisabled) {
            return;
        }
        this._displayValue = '';
        this.change.markForCheck();
    }

    OnCancel(event: Event): void {
        event.stopPropagation();
        this._close();
    }

    OnBackdrop(event: Event): void {
        event.stopPropagation();
        this._close();
    }

    /****************************/
    /* APPLY COUNT              */
    /****************************/

    private _applyCount = 1;

    private get minApply(): number {
        return 1;
    }

    private get maxApply(): number {
        return Math.max(this.minApply, Math.floor(this.lineAmount));
    }


    private get applyCount(): number {
        return this._applyCount;
    }

    private set applyCount(count: number) {
        this._applyCount = Math.min(this.maxApply, Math.max(this.minApply, Math.round(count)));
        this.change.markForCheck();
    }

    /****************************/
    /* LINE INFORMATION         */
    /****************************/

    private isWeighted(): boolean {
        return this.ticketproduct?.product?.IsWeighted ?? false;
    }

    private groupedLines(): Array<TicketProductView> {
        if (!this.ticketproduct) {
            return [];
        }

        let head = this.ticketproduct;
        while (head.DisplayTo) {
            head = head.DisplayTo;
        }

        const lines: Array<TicketProductView> = [];
        for (let node: TicketProductView | null = head; node; node = node.DisplayFr) {
            lines.push(node);
        }

        return lines;
    }

    private targetLines(): Array<TicketProductView> {
        if (!this.ticket || !this.ticketproduct) {
            return [];
        }

        const grouped = this.groupedLines();
        if (grouped.length === 0) {
            return [];
        }

        // qty applies only to the selected line
        if (this._field === 'amount') {
            return [ this.ticketproduct ];
        }

        // single-row target or full-row target
        if ((this.lineAmount <= 1) || (this.applyCount >= this.maxApply)) {
            return grouped;
        }

        // partial target: peel one by one using TicketView.SplitProduct
        let head = this.ticketproduct;
        while (head.DisplayTo) {
            head = head.DisplayTo;
        }

        const targets: Array<TicketProductView> = [];
        for (let idx = 0; idx < this.applyCount; idx++) {
            const ticketproduct = this.ticket.SplitProduct(head);
            if (ticketproduct) {
                targets.push(ticketproduct);
            }
        }

        return targets;
    }

    private get lineAmount(): number {
        let total = 0;

        const lines = this.groupedLines();
        for (const node of lines) {
            total += node.Count || node.amount || 0;
        }

        return total;
    }

    /****************************/
    /* LINE ACTIONS             */
    /****************************/

    OnAmount(event: Event): void {
        event.stopPropagation();
        if (this._field === 'invite' || this._field === 'price') {
            this._displayValue = '';
        }
        this._field = 'amount';
        this.change.markForCheck();
    }

    // Backward-compatible aliases used by existing unit tests/contracts.
    OnQty(event: Event): void {
        this.OnAmount(event);
    }

    OnPrice(event: Event): void {
        event.stopPropagation();
        if (!this.IsEnabled('price')) {
            return;
        }
        if (this._field !== 'price') {
            this._displayValue = '';
        }
        this._field = 'price';
        this.change.markForCheck();
    }

    OnPercent(event: Event): void {
        event.stopPropagation();
        if (!this.IsEnabled('percent')) {
            return;
        }
        if (this._field === 'invite') {
            this._displayValue = '';
        }
        this._field = 'percent';
        this.change.markForCheck();
    }

    OnInvite(event: Event): void {
        event.stopPropagation();
        if (this._field !== 'invite') {
            this._displayValue = '';
        }
        this._field = 'invite';
        this.change.markForCheck();
    }

    private toggleInvite(): boolean {
        const targets = this.targetLines();
        if (targets.length === 0) {
            return false;
        }

        const _invited = this.ticketproduct?.IsInvite ?? false;
        for (const line of targets) {
            if (_invited) {
                line.fixed = false;
                line.charge = null;
            } 
            else {
                line.fixed = true;
                line.charge = 0;
            }
        }

        return true;
    }

    private applyQty(value: number): boolean {
        const targets = this.targetLines();
        if (targets.length === 0) {
            return false;
        }

        for (const line of targets) {
            if (this.isWeighted()) {
                line.weight = Math.round(value * 1000);
            } 
            else if (this.ticket) {
                this.ticket.SetAmount(line, Math.round(value * 100) / 100);
            }
        }

        return true;
    }

    private applyPrice(value: number): boolean {
        const targets = this.targetLines();
        if (targets.length === 0) {
            return false;
        }

        for (const line of targets) {
            line.fixed = true;
            line.charge = Math.round(value * 100) / 100;
        }

        return true;
    }

    private applyPercent(value: number): boolean {
        const targets = this.targetLines();
        if (targets.length === 0) {
            return false;
        }

        for (const line of targets) {
            const percent = Math.min(100, Math.max(0, Math.round(value * 10) / 10));
            const charge = Math.round((line.price ?? 0) * (1 - percent / 100) * 100) / 100;
            
            line.fixed = true;
            line.charge = charge
        }

        return true;
    }

    private clearFixed(): boolean {
        const targets = this.targetLines();
        if (targets.length === 0) {
            return false;
        }

        let changed = false;
        for (const line of targets) {
            if (line.fixed || line.charge !== null) {
                line.fixed = false;
                line.charge = null;
                changed = true;
            }
        }

        return changed;
    }

    get CanSubmit(): boolean {
        if (this._field === 'invite') {
            return true;
        }

        if (this._field === 'price') {
            return this.IsEnabled('price');
        }

        if (this._field === 'percent') {
            return this.IsEnabled('percent');
        }
        
        return this._displayValue.trim() !== '';
    }

    OnSubmit(event: Event): void {
        event.stopPropagation();
        if (this._ispressed) {
            this._ispressed = false;
            return;
        }

        let updated = false;
        if (this._field === 'invite') {
            if (this.toggleInvite()) {
                updated = true;
            }
        }

        const value = this._parseNumber(this._displayValue.trim());
        if (value === null) {
            if ((this._field === 'price' && this.IsEnabled('price')) || (this._field === 'percent' && this.IsEnabled('percent'))) {
                if (this.clearFixed()) {
                    updated = true;
                }
            }
        } else {
            if (this._field === 'amount') {
                if (this.applyQty(value)){
                    updated = true;
                }
            }
    
            if ((this._field === 'price') && this.IsEnabled('price')) {
                if (this.applyPrice(value)){
                    updated = true;
                }
            }
    
            if ((this._field === 'percent') && this.IsEnabled('percent')) {
                if (this.applyPercent(value)){
                    updated = true;
                }
            }
        }

        if (updated) {  // recalculate ticket
            if (this.ticket) {  
                this.ticket.Reset();
           }
           
           this.Updated.emit();
        }

        this._close();
    }

    OnSubmitApplyAll(event: Event): void {
        this.applyCount = this.maxApply;
        this.OnSubmit(event);
    }

    OnSubmitApplyOne(event: Event): void {
        this.applyCount = this.minApply;
        this.OnSubmit(event);
    }

    /****************************/
    /* ADD/DEL ICONS            */
    /****************************/

    get CanAddStep(): boolean {
        if (!this.ShowsApplyScope) {
            return false;
        }
        return this.applyCount < this.maxApply;
    }

    get CanDelStep(): boolean {
        if (!this.ShowsApplyScope) {
            return false;
        }
        return this.applyCount > this.minApply;
    }

    OnAddClick(event: Event): void {
        event.stopPropagation();
        if (this._ispressed) {
            this._ispressed = false;
            return;
        }

        if (this.ShowsApplyScope && this.CanAddStep) {
            this.applyCount = this.applyCount + 1;
            this.change.markForCheck();
        }
    }

    OnDelClick(event: Event): void {
        event.stopPropagation();
        if (this._ispressed) {
            this._ispressed = false;
            return;
        }

        if (this.ShowsApplyScope && this.CanDelStep) {
            this.applyCount = this.applyCount - 1;
            this.change.markForCheck();
        }
    }

    OnAddPressed(down: boolean, event: Event): void {
        event.stopPropagation();
        this._handlePress('add', down);
    }

    OnDelPressed(down: boolean, event: Event): void {
        event.stopPropagation();
        this._handlePress('del', down);
    }

    private _handlePress(side: 'add' | 'del', down: boolean): void {
        if (!this.ShowsApplyScope) {
            return;
        }

        if (down) {
            if (side === 'add' && !this.CanAddStep) {
                return;
            }
            if (side === 'del' && !this.CanDelStep) {
                return;
            }

            if (this._pressTimeout !== null) {
                clearTimeout(this._pressTimeout);
                this._pressTimeout = null;
            }

            this._pressSide = side;
            this._pressed = false;
            
            this._pressTimeout = setTimeout(() => {
                this._pressTimeout = null;
                this._pressed = true;
                this._ispressed = true;
                
                if (side === 'add') {
                    this.applyCount = this.maxApply;
                } 

                if (side === 'del') {
                    this.applyCount = this.minApply;
                }

                this.change.markForCheck();
            }, LONG_PRESS_MS);

            return;
        }

        const _side = this._pressSide;
        const _pressed = this._pressed;
        
        if (this._pressTimeout !== null) {
            clearTimeout(this._pressTimeout);
            this._pressTimeout = null;
        }
        this._pressSide = null;
        this._pressed = false;

        if (_pressed || _side !== side) {
            return;
        }
    }

    /****************************/
    /* PRIVATE METHODS          */
    /****************************/

    private _close(): void {
        if (this._pressTimeout !== null) {
            clearTimeout(this._pressTimeout);
            this._pressTimeout = null;
        }

        this._pressSide = null;
        this._pressed = false;
        this._displayValue = '';
        this._field = 'amount';
        this.applyCount = this.minApply;

        this.Closed.emit();
        this.change.markForCheck();
    }

    private _parseNumber(raw: string): number | null {
        if (this._field === 'price') {
            const centsDigits = raw.replace(/\D/g, '');
            if (centsDigits === '') {
                return null;
            }
            const cents = Number(centsDigits);
            if (!Number.isFinite(cents)) {
                return null;
            }
            return Math.round(cents) / 100;
        }

        const normalized = raw.trim().replace(/,/g, '.');
        if (normalized === '' || normalized === '.' || normalized === '-') {
            return null;
        }
        const n = Number(normalized);
        return Number.isFinite(n) ? n : null;
    }

    private _formatPriceDisplay(centsDigits: string): string {
        const digits = (centsDigits || '').replace(/\D/g, '');
        if (digits === '') {
            return '';
        }
        
        const value = Number(digits) / 100;
        if (!Number.isFinite(value)) {
            return '';
        }

        return value.toFixed(2).replace('.', ',');
    }

    private _maxFractionDigits(): number {
        if (this._field === 'price') {
            return 0;
        }

        if (this._field === 'percent') {
            return 1;
        }

        if (this._field === 'amount') {
            return 2;
        }

        return 0;
    }

    private _isPercentOverLimit(raw: string): boolean {
        const value = this._parseNumber(raw);
        if (value === null) {
            return false;
        }

        return value > 100;
    }
}
